import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import DashboardLayout from "@/components/DashboardLayout";
import { YIELD_RATE } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  UserPlus, Users, Phone, Search, Trash2, Pencil, PackagePlus, Boxes, Scale, Plus, Minus, X, Droplets, Receipt,
} from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [orderClient, setOrderClient] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", phone: "", email: "", notes: "" }); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, phone: c.phone || "", email: c.email || "", notes: c.notes || "" }); setDialogOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success(t("save"));
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/clients/${id}`); toast.success(t("delete")); load(); };

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()) || (c.phone || "").includes(query));

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-5xl space-y-6" data-testid="clients-page">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{t("clients_title")}</h1>
            <p className="mt-1 text-muted-foreground">{t("clients_sub")}</p>
          </div>
          <Button data-testid="add-client-btn" onClick={openNew} className="rounded-full bg-primary hover:bg-olive-hover">
            <UserPlus className="mr-1.5 h-4 w-4" /> {t("add_client")}
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="client-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search")} className="pl-9" />
        </div>

        {filtered.length ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c) => (
              <div key={c.id} data-testid={`client-card-${c.id}`} className="rounded-xl border border-border bg-card p-5 transition-all hover:shadow-md">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-amber text-lg font-bold text-olive">
                      {c.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold">{c.name}</p>
                      {c.phone && <p className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" /> {c.phone}</p>}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">{c.order_count} {t("orders_label")}</p>
                <div className="mt-4 flex gap-2">
                  <Button data-testid={`new-batch-${c.id}`} size="sm" onClick={() => setOrderClient(c)} className="flex-1 rounded-lg bg-olive-light text-olive hover:bg-olive hover:text-primary-foreground">
                    <PackagePlus className="mr-1 h-3.5 w-3.5" /> {t("qa_new_order")}
                  </Button>
                  <Button data-testid={`edit-client-${c.id}`} size="icon" variant="outline" onClick={() => openEdit(c)} className="h-9 w-9"><Pencil className="h-3.5 w-3.5" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-client-${c.id}`} size="icon" variant="outline" className="h-9 w-9 text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>{t("delete")} {c.name}?</AlertDialogTitle>
                        <AlertDialogDescription>This will also delete all their batches.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-delete-${c.id}`} onClick={() => remove(c.id)} className="bg-destructive hover:bg-destructive/90">{t("delete")}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border py-16 text-center">
            <Users className="mx-auto h-10 w-10 text-muted-foreground/50" />
            <p className="mt-3 text-sm text-muted-foreground">{t("no_clients")}</p>
          </div>
        )}
      </div>

      {/* Client dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("add_client")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{t("client_name")}</Label>
              <Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("phone")}</Label>
              <Input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("email")} ({t("optional")})</Label>
              <Input data-testid="client-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("notes")}</Label>
              <Textarea data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-client-btn" onClick={save} disabled={!form.name} className="bg-primary hover:bg-olive-hover">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {orderClient && (
        <NewBatchDialog client={orderClient} onClose={() => setOrderClient(null)} onDone={() => { setOrderClient(null); load(); navigate("/queue-management"); }} t={t} />
      )}
    </DashboardLayout>
  );
}

function NewBatchDialog({ client, onClose, onDone, t }) {
  const [mode, setMode] = useState("per_crate");
  const [crates, setCrates] = useState([{ number: "", weight: "" }]);
  const [totalWeight, setTotalWeight] = useState("");
  const [crateCount, setCrateCount] = useState("");
  const [notes, setNotes] = useState("");
  const [manualYield, setManualYield] = useState("");
  const [showYield, setShowYield] = useState(false);
  const [generateInvoice, setGenerateInvoice] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/crates/next-number").then((r) => setCrates([{ number: String(r.data.next), weight: "" }])).catch(() => {});
  }, []);

  const addCrate = () => setCrates((c) => [...c, { number: String(Math.max(0, ...c.map((x) => parseInt(x.number, 10) || 0)) + 1), weight: "" }]);
  const removeCrate = (i) => setCrates((c) => c.filter((_, idx) => idx !== i));
  const setWeight = (i, v) => setCrates((c) => c.map((x, idx) => (idx === i ? { ...x, weight: v } : x)));
  const setCrateNumber = (i, v) => setCrates((c) => c.map((x, idx) => (idx === i ? { ...x, number: v } : x)));

  const computedTotal = mode === "per_crate"
    ? crates.reduce((a, c) => a + (parseFloat(c.weight) || 0), 0)
    : parseFloat(totalWeight) || 0;

  const submit = async () => {
    if (mode === "per_crate") {
      const nums = crates.map((c) => String(c.number).trim());
      if (nums.some((n) => !/^\d+$/.test(n))) { toast.error(t("crate_number_required")); return; }
      const ints = nums.map(Number);
      if (new Set(ints).size !== ints.length) { toast.error(t("crate_number_dup")); return; }
    }
    setBusy(true);
    try {
      const payload = {
        client_id: client.id, weight_mode: mode, notes,
        crates: mode === "per_crate" ? crates.map((c) => ({ number: parseInt(c.number, 10), weight: parseFloat(c.weight) || 0 })) : [],
        crate_count: mode === "total" ? parseInt(crateCount) || 0 : crates.length,
        total_weight: mode === "total" ? parseFloat(totalWeight) || 0 : 0,
        show_yield: showYield,
        invoice: generateInvoice,
        ...(showYield && manualYield !== "" ? { estimated_yield: parseFloat(manualYield) || 0 } : {}),
      };
      await api.post("/orders", payload);
      toast.success(t("batch_created"));
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="new-batch-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("new_batch_for")} {client.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="mode-per-crate" onClick={() => setMode("per_crate")}
              className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-medium transition-colors ${mode === "per_crate" ? "border-primary bg-olive-light text-olive" : "border-border"}`}>
              <Boxes className="h-4 w-4" /> {t("per_crate")}
            </button>
            <button data-testid="mode-total" onClick={() => setMode("total")}
              className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-medium transition-colors ${mode === "total" ? "border-primary bg-olive-light text-olive" : "border-border"}`}>
              <Scale className="h-4 w-4" /> {t("total_only")}
            </button>
          </div>

          {mode === "per_crate" ? (
            <div className="space-y-2">
              {crates.map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`crate-row-${i}`}>
                  <Input data-testid={`crate-number-${i}`} type="number" inputMode="numeric" min="1" placeholder={t("crate_number")} value={c.number} onChange={(e) => setCrateNumber(i, e.target.value)} className="w-28 shrink-0" />
                  <Input data-testid={`crate-weight-${i}`} type="number" inputMode="decimal" step="0.1" placeholder={t("weight_kg")} value={c.weight} onChange={(e) => setWeight(i, e.target.value)} className="flex-1" />
                  {crates.length > 1 && <Button size="icon" variant="ghost" onClick={() => removeCrate(i)} className="h-9 w-9 shrink-0"><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              <Button data-testid="add-crate-btn" variant="outline" size="sm" onClick={addCrate} className="w-full rounded-lg border-dashed"><Plus className="mr-1 h-4 w-4" /> {t("add_crate")}</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("crate_count")}</Label>
                <Input data-testid="total-crate-count" type="number" inputMode="numeric" value={crateCount} onChange={(e) => setCrateCount(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>{t("total_weight")}</Label>
                <Input data-testid="total-weight-input" type="number" inputMode="decimal" step="0.1" value={totalWeight} onChange={(e) => setTotalWeight(e.target.value)} /></div>
            </div>
          )}

          <div className="space-y-1.5"><Label>{t("notes")}</Label>
            <Textarea data-testid="batch-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

          <div className="flex items-center justify-between rounded-lg bg-olive-light px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-olive"><Scale className="h-4 w-4" /> {t("total_weight")}</span>
            <span className="font-heading text-lg font-bold text-olive">{computedTotal.toFixed(1)} kg</span>
          </div>

          <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-border px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold"><Droplets className="h-4 w-4 text-amber" /> {t("show_yield")}</span>
            <input data-testid="batch-show-yield-toggle" type="checkbox" checked={showYield} onChange={(e) => setShowYield(e.target.checked)} className="h-5 w-9 shrink-0 accent-green-700" />
          </label>

          {showYield && (
            <>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-olive-border px-4 py-3">
                <span className="flex items-center gap-2 text-sm font-semibold"><Droplets className="h-4 w-4 text-amber" /> {t("est_yield")}</span>
                <div className="flex items-center gap-1.5">
                  <Input data-testid="batch-yield-input" type="number" inputMode="decimal" step="0.1" min="0"
                    value={manualYield} placeholder={(computedTotal * YIELD_RATE).toFixed(1)}
                    onChange={(e) => setManualYield(e.target.value)} className="h-9 w-24 text-right font-heading font-bold" />
                  <span className="text-sm font-medium">{t("liters")}</span>
                </div>
              </div>
              <p className="-mt-2 text-xs text-muted-foreground">{t("yield_override_hint")}</p>
            </>
          )}
          <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-border px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold"><Receipt className="h-4 w-4 text-amber" /> {t("generate_invoice")}</span>
            <input data-testid="batch-invoice-toggle" type="checkbox" checked={generateInvoice} onChange={(e) => setGenerateInvoice(e.target.checked)} className="h-5 w-9 shrink-0 accent-green-700" />
          </label>
          {generateInvoice && <p className="-mt-2 text-xs text-muted-foreground">{t("invoice_hint")}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="create-batch-btn" onClick={submit} disabled={busy || computedTotal <= 0} className="bg-primary hover:bg-olive-hover">{t("create_batch")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
